import { Api } from 'further-types/further-user';
import { UserType } from 'further-types/user';

export enum FirmAccountResolutionKind {
  Idle = 'idle',
  Ok = 'ok',
  NeedsChooseAccount = 'needs_choose_account',
  NeedsCopyProfile = 'needs_copy_profile',
  AutoSwitch = 'auto_switch',
}

export type FirmAccountResolution =
  | { kind: FirmAccountResolutionKind.Idle }
  | { kind: FirmAccountResolutionKind.Ok }
  | { kind: FirmAccountResolutionKind.NeedsChooseAccount }
  | { kind: FirmAccountResolutionKind.NeedsCopyProfile }
  | {
      kind: FirmAccountResolutionKind.AutoSwitch;
      accountId: string;
      userType: UserType;
    };

export function resolveFirmAccountResolution(input: {
  enabled: boolean;
  activeAccountIds: Array<string>;
  accounts?: Api.ListAccountsToAccessResponse;
}): FirmAccountResolution {
  const { enabled, activeAccountIds, accounts } = input;

  if (!enabled || activeAccountIds.length === 0 || !accounts) {
    return { kind: FirmAccountResolutionKind.Idle };
  }

  const isMultiAccountToken = activeAccountIds.length > 1;
  if (isMultiAccountToken) {
    const hasOnlyOneInvestorAccount = accounts.investors.length === 1;
    const adviserAccount = accounts.adviser;
    const orgUserAccount = accounts.orgUser;
    const hasOnlyAdviserAccount =
      !accounts.investors.length && !!adviserAccount && !orgUserAccount;
    const hasOnlyOrgUserAccount =
      !accounts.investors.length && !adviserAccount && !!orgUserAccount;

    if (hasOnlyOneInvestorAccount) {
      return {
        kind: FirmAccountResolutionKind.AutoSwitch,
        accountId: accounts.investors[0]._id,
        userType: UserType.Investor,
      };
    }

    if (hasOnlyAdviserAccount && adviserAccount) {
      return {
        kind: FirmAccountResolutionKind.AutoSwitch,
        accountId: adviserAccount._id,
        userType: UserType.Adviser,
      };
    }

    if (hasOnlyOrgUserAccount && orgUserAccount) {
      return {
        kind: FirmAccountResolutionKind.AutoSwitch,
        accountId: orgUserAccount._id,
        userType: UserType.Organisation,
      };
    }

    const hasNoAccountsOnFirm =
      !accounts.investors.length && !adviserAccount && !orgUserAccount;

    if (hasNoAccountsOnFirm) {
      return { kind: FirmAccountResolutionKind.NeedsCopyProfile };
    }

    return { kind: FirmAccountResolutionKind.NeedsChooseAccount };
  }

  const activeAccountId = activeAccountIds[0];
  const isValidForFirm = accounts.investors.some(
    (inv) => inv._id === activeAccountId,
  );

  if (isValidForFirm) {
    return { kind: FirmAccountResolutionKind.Ok };
  }

  const hasOnlyOneInvestorAccount =
    accounts.investors.length === 1 && !accounts.adviser && !accounts.orgUser;

  if (hasOnlyOneInvestorAccount) {
    return {
      kind: FirmAccountResolutionKind.AutoSwitch,
      accountId: accounts.investors[0]._id,
      userType: UserType.Investor,
    };
  }

  const hasNoAccountsOnFirm =
    accounts.investors.length === 0 && !accounts.adviser && !accounts.orgUser;

  if (hasNoAccountsOnFirm) {
    return { kind: FirmAccountResolutionKind.NeedsCopyProfile };
  }

  // Active account is not valid for the firm and there is more than one option.
  return { kind: FirmAccountResolutionKind.NeedsChooseAccount };
}
